/**
 * mergePickedPosition — what happens to the form when the operator picks an
 * address from the Lantmäteriet search list (GF-77).
 *
 * WHY THIS IS A FUNCTION AND NOT THREE LINES INSIDE THE JSX: the rule it
 * encodes is "the pick sets the position and NOTHING else", and a rule that
 * matters has to be testable without rendering anything. What the box DISPLAYS
 * is not the guarantee — what gets SENT is. Binding a guard to the rendered
 * text would be testing the screen, which in this codebase has lied before.
 *
 * THE RULE, and the measurement behind it. Before this, picking a suggestion
 * wrote the PROVIDER'S text into the address field. GF-65 measured the same
 * overwrite in the ConfirmAddress modal: 514 production rows carry an address a
 * provider replaced, the original is stored nowhere, and only a re-import
 * brings it back. Lantmäteriet's answer is a register designation
 * ("Staffanstorp Staffanstorp Vikhemsvägen 2 24543 Staffanstorp") which is
 * measurably worse as stored text than what the register or the operator wrote
 * — the register is master, GeoFix is a transit step.
 *
 * ⚠️ BOTH PLACES, AND THEY ARE NOT REDUNDANT:
 *
 *   lat / lng            the two form fields handleSubmit actually persists
 *   location.latLng      what the field validator reads as "picked"
 *
 * Setting only the first gives "Invalid" with no explanation — the worst kind
 * of error, because nothing on screen says what is wrong. Setting only the
 * second saves the old coordinates while the form looks correct.
 */

/**
 * @param currentLocation the form's existing `location` value, or null
 * @param position        { lat, lng } from the search list — NEVER any text
 * @returns the patch to hand to form.setFieldsValue
 */
export function mergePickedPosition(currentLocation, position) {
  const current = currentLocation || {};
  return {
    location: {
      // Spread FIRST and never assign `address` below: whatever text the field
      // holds is the operator's, and it survives the pick untouched. This is
      // the line the test is guarding — changing it reopens GF-65.
      ...current,
      latLng: { lat: position?.lat ?? null, lng: position?.lng ?? null },
    },
    lat: position?.lat ?? null,
    lng: position?.lng ?? null,
  };
}

/**
 * What the search list hands onward when a row is clicked: A POSITION, AND
 * NOTHING ELSE.
 *
 * Separate from mergePickedPosition() because the protection has TWO halves
 * and they can fail independently:
 *
 *   1. the list must not SEND the provider's text  (this function)
 *   2. the merge must not READ it if it arrives    (mergePickedPosition)
 *
 * Either one alone would look sufficient right up until somebody changes the
 * other. Building both means a single edit cannot reopen GF-65 — and, equally
 * to the point, it means each half can be guarded on its own instead of a green
 * test standing for a rule that two different lines happen to satisfy.
 */
export function toPickedPosition(result) {
  return { lat: result?.lat ?? null, lng: result?.lng ?? null };
}
